import { type PlayerItemResponse } from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { SportIcon, type SportName } from 'components/Icons/SportIcon';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { divisionAbbreviation } from 'components/Nil/helpers';
import { ArrowRight } from 'components/Svg/ArrowRight';
import { AthleteVerified } from 'components/UI/AthleteVerified/AthleteVerified';
import { StarRatings } from 'components/UI/StarRatings/StarRatings';
import { format } from 'date-fns';

import styles from './LatestCommitsPlayer.module.scss';

export const LatestCommitsPlayer = ({ data }: { data: PlayerItemResponse }) => {
  const { rating, status, player, transferRating, rosterRating } = data;
  const {
    slug,
    height,
    defaultAsset,
    weight,
    fullName,
    athleteVerified,
    position,
    division,
    defaultSport,
    highSchool,
    hometown,
  } = player ?? {};

  const activeRating = transferRating || rosterRating || rating;
  const sportName = (rating?.sport?.name || defaultSport?.name) as SportName;
  const hsUrl =
    !!highSchool?.slug && !!highSchool?.urlSlug
      ? `/high-school/${highSchool.urlSlug}/`
      : null;

  return (
    <div className={styles.block}>
      <div className={styles.playerContainer}>
        <Link className={styles.avatarContainer} href={`/rivals/${slug}/`}>
          <Avatar
            altText={`${fullName} Avatar`}
            className={styles.playerAvatar}
            domain={defaultAsset?.domain}
            fit="cover"
            height={50}
            source={defaultAsset?.source}
            width={50}
          />
        </Link>
        <div className={styles.info}>
          {fullName && (
            <div className={styles.player}>
              <Link className={styles.name} href={`/rivals/${slug}/`}>
                {fullName}
              </Link>
              {athleteVerified && (
                <AthleteVerified className={styles.verified} />
              )}
            </div>
          )}
          <div aria-label="Player Vitals" className={styles.vitals}>
            <SportIcon className={styles.sportIcon} sportName={sportName} />
            {!!division && <span>{divisionAbbreviation(division)}</span>}
            {!!position?.abbr && <span>{position.abbr}</span>}
            {!!height && <span>{height}</span>}
            {!!weight && <span>{weight}</span>}
          </div>
          <div className={styles.rating}>
            <Link href={`/rivals/${slug}/industry-comparison/`}>
              <StarRatings stars={activeRating?.stars ?? 0} />
              <span className="sr-only">View Industry Comparison</span>
            </Link>
            {activeRating?.rating && (
              <span>{activeRating.rating.toFixed(2)}</span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.statusContainer}>
        <span className={styles.date}>
          {status?.date && format(new Date(status.date), 'P')}
        </span>
        {status?.transferredAsset?.assetUrl && (
          <>
            <TeamAvatar
              className={styles.teamAvatar}
              height={20}
              src={status.transferredAsset.assetUrl}
              teamName={
                status.transferredAsset.knownAs || status.transferredAsset.name
              }
              width={20}
            />
            <ArrowRight />
          </>
        )}
        {status?.committedAsset?.assetUrl && (
          <TeamAvatar
            className={styles.teamAvatar}
            height={20}
            src={status.committedAsset.assetUrl}
            teamName={
              status.committedAsset.knownAs || status.committedAsset.name
            }
            width={20}
          />
        )}
      </div>

      {(!!highSchool || !!hometown?.name) && (
        <div className={styles.locationContainer}>
          {!!highSchool && (
            <Link className={styles.highSchoolContainer} href={hsUrl}>
              <Avatar
                altText={`${highSchool.name} Avatar`}
                className={styles.highSchoolAvatar}
                domain={highSchool.defaultAsset?.domain}
                fit="contain"
                height={50}
                source={highSchool.defaultAsset?.source}
                teamAvatar
                width={50}
              />
              <span>{highSchool.name}</span>
            </Link>
          )}
          {hometown?.name && (
            <span className={styles.hometown}>{hometown.abbr}</span>
          )}
        </div>
      )}
    </div>
  );
};
